<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="main-header mb-0">
        <translate v-if="scopeType === 'global'" translate-context="collaborators.list">Global Collaborators</translate>
        <translate v-else-if="scopeType === 'contract-manager'" translate-context="collaborators.list">Contract Manager Users</translate>
        <translate v-else translate-context="collaborators.list">Users</translate>
      </h1>
      <CButton
        color="primary"
        variant="outline"
        size="sm"
        @click="refreshCollaborators"
      >
        <CIcon name="cilReload" size="sm" class="mr-1"/>
        <translate translate-context="collaborators.list">Update list</translate>
      </CButton>
    </div>
    
    <InviteBlock
      ref="inviteBlock"
      v-if="canInvite"
      :scope="scope"
      :available-roles="availableRoles"
      @invited="onInvitationSent"
      @open-invite-role-modal="openInviteRoleModal"
    />
    
    <CRow class="mb-3">
      <CCol>
        <CCard class="no-borders">
          <CCardHeader class="px-2">
            <div class="d-flex justify-content-end align-items-center" style="min-height: 42px;">
              <div class="card-header-actions">
                <CButton
                  v-if="canExportFtp"
                  color="primary"
                  variant="outline"
                  @click="openExportFtpModal"
                  class="mr-2"
                >
                  <translate translate-context="collaborators.list">Export FTP</translate>
                </CButton>

                <CButton
                  v-show="hasPendingChanges"
                  color="danger"
                  variant="outline"
                  @click="cancelChanges"
                  class="mr-2 pending-changes-btn"
                >
                  <translate translate-context="collaborators.list">Cancel Changes</translate>
                </CButton>

                <CButton
                  v-show="hasPendingChanges"
                  color="success"
                  @click="applyChanges"
                  class="mr-2 pending-changes-btn"
                >
                  <translate translate-context="collaborators.list">Apply Changes</translate> ({{ totalPendingChanges }})
                </CButton>

              </div>
            </div>
          </CCardHeader>
          
          <CCardBody class="px-0">
            <div class="table-responsive custom-table" v-if="isReady">
              <AjaxTable
                :fields="fields"
                :data="collaborators"
                :fetch="fetchList"
                ref="ajaxTable"
              >
                <template #email="{item}">
                  <td :class="getRowClass(item)">
                    <div class="d-flex align-items-center">
                      <CButton
                        v-if="canViewPermissions"
                        color="link"
                        size="sm"
                        @click="showUserPermissions(item)"
                        :title="$gettext('View user permissions')"
                        class="flex-shrink-0"
                      >
                        <CIcon name="cilShieldAlt" size="sm"/>
                      </CButton>

                      <div class="flex-grow-1 text-truncate">
                        {{ item.email }}
                        <span v-if="item.first_name || item.last_name" class="text-muted ml-2">
                          ({{ item.first_name }} {{ item.last_name }})
                        </span>

                        <CBadge v-if="item.is_inherited" color="info" class="ml-2">
                          <translate translate-context="collaborators.list" :translate-params="{ source: getInheritedSource(item) }">Inherited from %{ source }</translate>
                        </CBadge>
                        <CBadge v-if="pendingChanges[item.id] && Object.keys(pendingChanges[item.id]).length > 0" color="warning" class="ml-2">
                          <CIcon name="cilWarning" size="sm" class="mr-1"/>
                          <translate translate-context="collaborators.list">Modified</translate>
                        </CBadge>
                      </div>
                    </div>
                  </td>
                </template>
                
                <template #roles="{item}">
                  <td :class="getRowClass(item)">
                    <RoleSelector
                      :collaborator="item"
                      :available-roles="availableRoles"
                      :pending-changes="pendingChanges[item.id]"
                      :disabled="!canAssignRoles"
                      :is-inherited-user="item.is_inherited"
                      @toggle="toggleRole"
                      @view-permissions="showRolePermissions"
                      @open-role-modal="openRoleModal(item)"
                    />
                  </td>
                </template>
                
                <template #status-filter>
                  <CSelect
                    size="sm"
                    class="mb-0"
                    :placeholder="$gettext('Select')"
                    :options="getStatusFilterOptions"
                    v-model="filters.status"
                    @change="onStatusFilterChange"
                  />
                </template>
                
                <template #status="{item}">
                  <td :class="getRowClass(item)">
                    <CBadge :color="CollaboratorStatus.color(item.status)">
                      {{ CollaboratorStatus.label(item.status, 'collaborators.list') }}
                    </CBadge>
                  </td>
                </template>
                
                <template #invited_at="{item}">
                  <td :class="getRowClass(item)">
                    <div class="text-muted small">
                      {{ formatDate(item.invited_at) }}
                      <div v-if="item.invited_by_email" class="text-info" style="font-size: 11px;">
                        {{ $gettext('by') }} {{ item.invited_by_email }}
                      </div>
                    </div>
                  </td>
                </template>
                
                <template #accepted_at="{item}">
                  <td :class="getRowClass(item)">
                    <div v-if="item.accepted_at" class="text-muted small">
                      {{ formatDate(item.accepted_at) }}
                    </div>
                    <div v-else-if="item.status === CollaboratorStatus.PENDING" class="text-warning small">
                      <CIcon name="cilClock" size="sm"/>
                      <translate translate-context="collaborators.list">Waiting</translate>
                    </div>
                    <div v-else class="text-muted small">-</div>
                  </td>
                </template>
                
                <template #actions="{item}">
                  <td>
                    <div class="d-flex justify-content-end mr-2">
                      <div class="btn-group">
                        <CButton
                          v-if="CollaboratorStatus.canResendInvitation(item.status) && canUpdateEmail"
                          size="sm"
                          color="warning"
                          @click="openEditEmailModal(item)"
                          v-c-tooltip="$gettext('Edit email')"
                        >
                          <CIcon name="cilPencil" size="sm"/>
                        </CButton>
                        
                        <CButton
                          v-if="CollaboratorStatus.canResendInvitation(item.status) && canResendInvitation"
                          size="sm"
                          color="info"
                          @click="confirmResendInvitation(item)"
                          v-c-tooltip="$gettext('Resend invitation')"
                        >
                          <CIcon name="cipSend" size="sm"/>
                        </CButton>
                        
                        <CButton
                          v-if="CollaboratorStatus.canResendInvitation(item.status) && canBypassAccept"
                          size="sm"
                          color="success"
                          @click="confirmForceAccept(item)"
                          v-c-tooltip="$gettext('Force accept invitation')"
                        >
                          <CIcon name="cilCheckCircle" size="sm"/>
                        </CButton>
                        
                        <CButton
                          v-if="CollaboratorStatus.canEnable(item.status) && canEnableDisable"
                          size="sm"
                          color="success"
                          @click="enableCollaborator(item.id)"
                          v-c-tooltip="$gettext('Enable')"
                        >
                          <CIcon name="cipCircleCheck" size="sm"/>
                        </CButton>
                        
                        <CButton
                          v-if="CollaboratorStatus.canDisable(item.status) && !item.is_inherited && canEnableDisable"
                          size="sm"
                          color="danger"
                          @click="confirmDisable(item)"
                          v-c-tooltip="$gettext('Disable')"
                        >
                          <CIcon name="cipXCircle" size="sm"/>
                        </CButton>
                      </div>
                    </div>
                  </td>
                </template>
                
              </AjaxTable>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    
    <InviteModal
      ref="inviteModal"
      :available-roles="availableRoles"
      :scope="scope"
      @sent="onInvitationSent"
    />
    
    <UserPermissionModal
      ref="userPermissionModal"
      :scope="scope"
    />
    
    <RolePermissionsModal
      ref="rolePermissionsModal"
      :scope="scope"
    />
    
    <RoleSelectionModal
      ref="roleSelectionModal"
      :available-roles="availableRoles"
      :scope-type="scopeType"
      @view-permissions="showRolePermissions"
      @toggle="toggleRole"
    />
    
    <EditEmailModal
      ref="editEmailModal"
      :scope="scope"
      @updated="onEmailUpdated"
    />

    <ExportFtpModal
      ref="exportFtpModal"
      :scope-type="scopeType"
    />
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import RoleSelector from './RoleSelector.vue'
import InviteModal from './InviteModal.vue'
import InviteBlock from './InviteBlock.vue'
import UserPermissionModal from './UserPermissionModal.vue'
import RolePermissionsModal from './RolePermissionsModal.vue'
import RoleSelectionModal from './RoleSelectionModal.vue'
import EditEmailModal from './EditEmailModal.vue'
import ExportFtpModal from './ExportFtpModal.vue'
import CollaboratorStatus from '@/domain/core/constant/collaboratorStatus'

export default {
  name: 'Collaborators',

  components: {
    AjaxTable,
    RoleSelector,
    InviteModal,
    InviteBlock,
    UserPermissionModal,
    RolePermissionsModal,
    RoleSelectionModal,
    EditEmailModal,
    ExportFtpModal
  },
  
  props: {
    scopeType: {
      type: String,
      required: true,
      validator: v => ['global', 'contract-manager', 'pica-service'].includes(v)
    },
    scopeId: {
      type: [String, Number],
      default: null
    }
  },
  
  data() {
    return {
      CollaboratorStatus,
      filters: {
        search: '',
        status: null
      },
      isReady: false
    }
  },
  
  computed: {
    ...mapGetters('collaborators', [
      'collaborators',
      'availableRoles',
      'pendingChanges',
      'hasPendingChanges',
      'getPendingChangesForCollaborator',
      'pendingInvitations'
    ]),
    
    getRoleFilterOptions() {
      // Crea opzioni di filtro dai ruoli disponibili
      if (!this.availableRoles || this.availableRoles.length === 0) {
        return []
      }
      return this.availableRoles.map(role => ({
        label: role.display_name || role.name,
        value: role.name // Use role name instead of ID for filtering
      }))
    },
    
    fields() {
      return [
        {key: 'email', label: this.$gettext('User'), _style: 'width: 280px'},
        {key: 'roles', label: this.$gettext('Roles'), sorter: false, filter: {type: 'select', values: this.getRoleFilterOptions}},
        {key: 'status', label: this.$gettext('Status'), _style: 'width: 120px', filter: {lazy: false, external: true}},
        {key: 'invited_at', label: this.$gettext('Invited'), _style: 'width: 120px', filter: false},
        {key: 'accepted_at', label: this.$gettext('Accepted'), _style: 'width: 120px', filter: false},
        {key: 'actions', label: '', sorter: false, filter: false, _style: 'width: 140px; text-align: right'}
      ]
    },
    
    getStatusFilterOptions() {
      return CollaboratorStatus.filterOptions(true, 'collaborators.status.filter')
    },
    
    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId
      }
    },
    
    totalPendingChanges() {
      // Conta il numero totale di modifiche (non il numero di collaboratori)
      let count = 0
      Object.values(this.pendingChanges).forEach(changes => {
        count += Object.keys(changes).length
      })
      return count
    },
    
    canInvite() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.invite')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.invite')
      }
      return this.hasPerm('admin.collaborator.invite')
    },
    
    canDelete() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.delete')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.delete')
      }
      return this.hasPerm('admin.collaborator.delete')
    },
    
    canUpdate() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.update')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.update')
      }
      return this.hasPerm('admin.collaborator.update')
    },
    
    canAssignRoles() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.assign_roles')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.assign_roles')
      }
      return this.hasPerm('admin.collaborator.assign_roles')
    },
    
    canViewPermissions() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.show_user_permissions')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.show_user_permissions')
      }
      return this.hasPerm('admin.collaborator.show_user_permissions')
    },
    
    canViewRolePermissions() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.show_role_permissions')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.show_role_permissions')
      }
      return this.hasPerm('admin.collaborator.show_role_permissions')
    },
    
    canResendInvitation() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.resend_invitation')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.resend_invitation')
      }
      return this.hasPerm('admin.collaborator.resend_invitation')
    },
    
    canEnableDisable() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.enable_disable')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.enable_disable')
      }
      return this.hasPerm('admin.collaborator.enable_disable')
    },
    
    canUpdateEmail() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.update_email')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.update_email')
      }
      return this.hasPerm('admin.collaborator.update_email')
    },
    
    canBypassAccept() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.bypass_accept')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.bypass_accept')
      }
      return this.hasPerm('admin.collaborator.bypass_accept')
    },

    canExportFtp() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.export_credentials')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.export_credentials')
      }
      return false
    }
  },
  
  beforeRouteLeave(to, from, next) {
    // Avvisa se ci sono modifiche pendenti
    if (this.hasPendingChanges) {
      const answer = window.confirm(this.$gettext('You have unsaved changes. Do you really want to leave?'))
      if (!answer) {
        next(false)
        return
      }
    }
    next()
  },
  
  async mounted() {
    try {
      await this.setScope(this.scope)
      
      // Fetch config based on scope
      if (this.scopeType === 'pica-service') {
        await this.fetchConfig({
          type: 'collaborators_pica_service',
          id: this.scopeId
        })
      } else if (this.scopeType === 'contract-manager') {
        await this.fetchConfig({
          type: 'collaborators_contract_manager',
          id: this.scopeId
        })
      } else {
        await this.fetchConfig({
          type: 'collaborators_global'
        })
      }
      
      await this.fetchAvailableRoles()
    } catch (error) {
      // Show error but still allow the page to render
      this.notifyError(this.$pgettext('collaborators.notification', 'Failed to load data'))
    } finally {
      this.isReady = true
    }
  },
  
  methods: {
    ...mapActions('collaborators', [
      'setScope',
      'fetchCollaborators',
      'fetchAvailableRoles',
      'applyPendingChanges',
      'cancelPendingChanges',
      'inviteCollaborator',
      'disableCollaborator',
      'enableCollaborator',
      'resendInvitation',
      'forceAcceptInvitation',
      'fetchRolePermissions',
      'updateFilters',
      'toggleRoleChange'
    ]),
    
    ...mapActions('login', [
      'fetchConfig'
    ]),
    
    async fetchList({filters = {}, pagination = {}} = {}) {
      // AjaxTable passa {filters, pagination, sorting}
      // Il filtro della colonna email viene passato come filters.email
      const searchValue = filters.email || this.filters.search || ''
      
      // Gestisci il filtro per status - usa il valore dal componente se disponibile
      const statusFilter = this.filters.status || null
      
      // Gestisci il filtro per ruolo - use role name directly
      const roleFilter = filters.roles || null
      
      await this.updateFilters({
        search: searchValue,
        status: statusFilter,
        roleName: roleFilter,
        page: pagination.page || 1,
        pageSize: pagination.page_size || 20
      })
    },
    
    async onStatusFilterChange() {
      // Quando cambia il filtro status, ricarica i dati
      await this.fetchList({ pagination: { page: 1 } })
    },
    
    async onSearchChange() {
      // Update filters with the current search value and reset to page 1
      await this.updateFilters({
        search: this.filters.search,
        page: 1
      })
    },
    
    getRowClass(item) {
      const classes = []
      if (item.status === CollaboratorStatus.DISABLED) classes.push('invitation-inactive')
      if (item.is_inherited) classes.push('invitation-inherited')
      return classes.join(' ')
    },
    
    getInheritedSource(item) {
      if (this.scopeType === 'pica-service') {
        return item.scope === 'GLOBAL' ? 'Global' : 'Contract Manager'
      }
      if (this.scopeType === 'contract-manager') {
        return 'Global'
      }
      return ''
    },
    
    async applyChanges() {
      try {
        await this.applyPendingChanges()
        await this.notifySuccess(this.$pgettext('collaborators.notification', 'Changes applied successfully'))
        await this.fetchCollaborators()
      } catch (e) {
        await this.notifyError(this.$pgettext('collaborators.notification', 'Failed to apply changes'))
      }
    },
    
    cancelChanges() {
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Cancel Changes'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to cancel all pending changes?'),
        cb_confirm: async () => {
          this.cancelPendingChanges()
          await this.notifySuccess(this.$pgettext('collaborators.notification', 'Changes cancelled'))
        }
      })
    },
    
    
    async confirmDisable(item) {
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Confirm Disable'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to disable this user?'),
        cb_confirm: async () => {
          try {
            await this.disableCollaborator(item.id)
            await this.notifySuccess(this.$pgettext('collaborators.notification', 'User disabled'))
            await this.fetchCollaborators()
          } catch (e) {
            await this.notifyError(this.$pgettext('collaborators.notification', 'Failed to disable user'))
          }
        }
      })
    },
    
    async confirmForceAccept(item) {
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Force Accept Invitation'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to force accept this invitation? This will create a user account with the provided email address.'),
        cb_confirm: async () => {
          try {
            await this.forceAcceptInvitation(item.id)
            await this.notifySuccess(this.$pgettext('collaborators.notification', 'Invitation forcefully accepted'))
            await this.fetchCollaborators()
          } catch (e) {
            if (e.response?.data?.code === 'invalid_collaborator_status') {
              await this.notifyError(this.$pgettext('collaborators.notification', 'Can only force accept pending invitations'))
            } else {
              await this.notifyError(this.$pgettext('collaborators.notification', 'Failed to force accept invitation'))
            }
          }
        }
      })
    },
    
    async confirmResendInvitation(item) {
      const emailMessage = this.$pgettext('collaborators.confirm', 'Send invitation email to') + ' ' + item.email + '?'
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Resend Invitation'),
        message: emailMessage,
        cb_confirm: async () => {
          try {
            await this.resendInvitation(item.id)
            const successMessage = this.$pgettext('collaborators.notification', 'Invitation email sent to') + ' ' + item.email
            await this.notifySuccess(successMessage)
            // No need to refresh the list, just sending an email
          } catch (e) {
            await this.notifyError(this.$pgettext('collaborators.notification', 'Failed to send invitation email'))
          }
        }
      })
    },
    
    async showUserPermissions(user) {
      // The user object already contains permissions and roles from the API response
      this.$refs.userPermissionModal.show(user)
    },
    
    async showRolePermissions(role) {
      const permissions = await this.fetchRolePermissions(role.id)
      this.$refs.rolePermissionsModal.show(permissions, role)
    },
    
    openRoleModal(collaborator) {
      this.$refs.roleSelectionModal.show({
        title: this.$pgettext('collaborators.modal', 'Manage User Roles'),
        roles: collaborator.roles || [],
        pendingChanges: this.pendingChanges[collaborator.id] || {},
        isInheritedUser: collaborator.is_inherited,
        collaboratorId: collaborator.id
      })
    },
    
    openInviteRoleModal(data) {
      // Apre la modale per selezionare i ruoli nell'invito
      this.$refs.roleSelectionModal.show({
        title: this.$pgettext('collaborators.modal', 'Select Roles for Invitation'),
        selectedRoles: data.selectedRoles || {},
        callback: (selectedRoles) => {
          // Aggiorna i ruoli selezionati nell'InviteBlock
          if (this.$refs.inviteBlock) {
            this.$refs.inviteBlock.updateSelectedRoles(selectedRoles)
          }
        }
      })
    },
    
    openEditEmailModal(collaborator) {
      // Apre la modale per modificare l'email del collaboratore
      this.$refs.editEmailModal.show(collaborator)
    },
    
    async onEmailUpdated() {
      // Ricarica la lista dopo l'aggiornamento dell'email
      await this.fetchCollaborators()
    },
    
    // Chiamato dal mixin quando i permessi cambiano
    async onInvitationSent() {
      await this.fetchCollaborators()
    },
    
    async refreshCollaborators() {
      await this.fetchCollaborators()
      await this.notifySuccess(this.$pgettext('collaborators.notification', 'List updated'))
    },
    
    toggleRole(collaboratorId, roleId, value) {
      this.toggleRoleChange({ collaboratorId, roleId, value })
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) {
        return this.$pgettext('collaborators.date', 'Today')
      } else if (diffDays === 1) {
        return this.$pgettext('collaborators.date', 'Yesterday')
      } else if (diffDays < 7) {
        return this.$ngettext('%{n} day ago', '%{n} days ago', diffDays).replace('%{n}', diffDays)
      } else {
        return date.toLocaleDateString()
      }
    },

    openExportFtpModal() {
      this.$refs.exportFtpModal.open()
    }
  }
}
</script>

<style scoped>
.invitation-inactive {
  background-color: #f5f5f5;
  opacity: 0.8;
}

.invitation-inherited {
  background-color: #f0f9ff;
}

.invitation-new-row {
  background: linear-gradient(to bottom, #f0f9ff, #f9fafb);
  border-top: 3px solid #3b82f6;
}

.invitation-new-row td {
  padding: 15px 10px;
  vertical-align: middle;
}

.role-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
}

.role-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.role-indicator {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: white;
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.role-button.role-active .role-indicator {
  background: #10b981;
  border-color: #10b981;
}

.role-button.role-active .role-indicator::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 10px;
  font-weight: bold;
}
</style>

<style>
/* Forza le larghezze delle colonne - deep selector necessario per penetrare il componente AjaxTable */
.custom-table table {
  table-layout: fixed;
  width: 100%;
}

/* Colonna User */
.custom-table table th:nth-child(1),
.custom-table table td:nth-child(1) {
  width: 280px !important;
}

/* Colonna Roles - occupa lo spazio rimanente */
.custom-table table th:nth-child(2),
.custom-table table td:nth-child(2) {
  width: auto !important;
}

/* Colonna Actions */
.custom-table table th:nth-child(6),
.custom-table table td:nth-child(6) {
  width: 140px !important;
}
</style>